import type { ReactNode } from 'react';
import { motion, type Variants } from 'framer-motion';
import { cn } from '@/utils/cn';
import { spring } from '@/lib/motion';
import { useBackendStatus } from '@/contexts/BackendStatusContext';
import { ConnectionErrorState } from './ConnectionErrorState';

interface EmptyStateProps {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
  className?: string;
  /**
   * Si `true` (default), cuando el `BackendStatusContext` reporta que
   * el backend está caído o sin internet, este `EmptyState` se renderiza
   * como `<ConnectionErrorState>` automáticamente. Esto evita el bug de
   * "tabla vacía cuando en realidad la API falló". Páginas que quieren
   * mostrar el empty state literal (ej. resultados de búsqueda con 0
   * matches en una vista offline-cache) pueden setear `disableBackendStatusOverride={true}`.
   */
  disableBackendStatusOverride?: boolean;
}

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: spring.gentle },
};

const iconBadge: Variants = {
  hidden: { opacity: 0, scale: 0.6, rotate: -8 },
  visible: {
    opacity: 1,
    scale: 1,
    rotate: 0,
    transition: spring.bouncy,
  },
};

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
  disableBackendStatusOverride = false,
}: EmptyStateProps) {
  const { status, retry, isRetrying } = useBackendStatus();

  // Si la lista está vacía PORQUE el backend está caído / sin internet,
  // no es un empty real — es un fallo de carga. Renderizamos el
  // ConnectionErrorState para que el usuario entienda la causa real
  // y pueda reintentar. Esto resuelve el caso reportado:
  //   "el backend está abajo y la página muestra una tabla vacía".
  if (!disableBackendStatusOverride && status !== 'ok') {
    return (
      <ConnectionErrorState
        variant={status === 'server-error' ? 'server' : 'network'}
        onRetry={retry}
        secondaryAction={
          isRetrying ? (
            <span className="text-xs text-muted-foreground">Probando…</span>
          ) : undefined
        }
        className={className}
      />
    );
  }

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="visible"
      className={cn(
        'flex flex-col items-center justify-center py-12 text-center',
        className,
      )}
    >
      {icon && (
        <motion.div
          variants={iconBadge}
          className="relative mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground"
        >
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full border border-border"
            style={{ animation: 'ring-pulse 2.4s ease-out infinite', color: 'var(--color-muted-foreground)' }}
          />
          <span className="relative">{icon}</span>
        </motion.div>
      )}
      <motion.h3
        variants={item}
        className="font-display text-lg tracking-tight text-foreground"
      >
        {title}
      </motion.h3>
      {description && (
        <motion.p
          variants={item}
          className="mt-1 max-w-sm text-sm text-muted-foreground"
        >
          {description}
        </motion.p>
      )}
      {action && (
        <motion.div variants={item} className="mt-5">
          {action}
        </motion.div>
      )}
    </motion.div>
  );
}
